import { FC, useState } from "react";
import { Alert, Badge, Button, Card, Modal, Spinner, ToggleSwitch } from "flowbite-react";
import { useTranslation } from "react-i18next";
import { toast } from "@/lib/toast";
import { HiClipboardCopy, HiExclamation, HiEye, HiEyeOff, HiRefresh } from "react-icons/hi";
import {
  useGetCompanyDetailsQuery,
  useGetSsoSettingsQuery,
  useUpdateSsoSettingsMutation,
  useRotateTenantSecretMutation,
} from "@/api/companyApi/company";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isAdminTierUser } from "@/constants/agent";
import { apiBaseUrl } from "@/config/api";

/**
 * Agent SSO configuration.
 *
 * Two things live here: whether an unrecognised identity may create an agent
 * account, and the tenant secret an integrator needs only if they sign tokens
 * themselves. Everything else about SSO is deliberately not configurable —
 * token lifetime and replay rules are ours to enforce, not a customer's to set.
 */
const SsoSettingsTab: FC = () => {
  const { t } = useTranslation(["companySetting", "common"]);
  const user = useCurrentUser();

  const isAdmin =
    isAdminTierUser(user);

  const { data: company, isLoading: loadingCompany } = useGetCompanyDetailsQuery();
  const companyId = company?.company_id;

  const {
    data: settings,
    isLoading: loadingSettings,
    isError,
  } = useGetSsoSettingsQuery(companyId as number, { skip: !companyId || !isAdmin });

  const [updateSettings, { isLoading: isSaving }] = useUpdateSsoSettingsMutation();
  const [rotateSecret, { isLoading: isRotating }] = useRotateTenantSecretMutation();

  const [secretVisible, setSecretVisible] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);

  if (!isAdmin) {
    return (
      <Alert color="warning" className="mt-4">
        {t("companySetting:sso.adminOnly")}
      </Alert>
    );
  }

  if (loadingCompany || loadingSettings) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="xl" />
      </div>
    );
  }

  if (isError || !settings || !companyId) {
    return (
      <Alert color="failure" className="mt-4">
        {t("companySetting:sso.loadError")}
      </Alert>
    );
  }

  const handleToggle = async (value: boolean) => {
    try {
      await updateSettings({ company_id: companyId, sso_auto_provision: value }).unwrap();
      toast.success(t("companySetting:sso.saved"));
    } catch {
      toast.error(t("companySetting:sso.saveError"));
    }
  };

  const handleRotate = async () => {
    try {
      await rotateSecret(companyId).unwrap();
      setConfirmRotate(false);
      setSecretVisible(true);
      toast.success(t("companySetting:sso.rotated"));
    } catch {
      toast.error(t("companySetting:sso.rotateError"));
    }
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("common:copied"));
    } catch {
      toast.error(t("companySetting:sso.copyError"));
    }
  };

  // Reuse the same base the app talks to, so the value shown is the one the
  // integrator should actually call rather than something reconstructed here.
  const loginUrlEndpoint = `${apiBaseUrl}/external/sso/login-url`;

  return (
    <div className="mt-4 flex flex-col gap-4">
      <Card>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t("companySetting:sso.autoProvision.title")}
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
              {t("companySetting:sso.autoProvision.description")}
            </p>
          </div>
          <ToggleSwitch
            checked={settings.sso_auto_provision}
            disabled={isSaving}
            onChange={handleToggle}
            label=""
          />
        </div>

        {settings.sso_auto_provision && (
          <Alert color="warning" icon={HiExclamation}>
            {t("companySetting:sso.autoProvision.warning")}
          </Alert>
        )}
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("companySetting:sso.secret.title")}
        </h3>
        <p className="-mt-2 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
          {t("companySetting:sso.secret.description")}
        </p>

        {settings.tenant_secret ? (
          <>
            <div className="flex items-center gap-2">
              <code className="flex-1 overflow-x-auto rounded bg-gray-100 px-3 py-2 font-mono text-sm dark:bg-gray-700 dark:text-gray-200">
                {secretVisible ? settings.tenant_secret : "•".repeat(48)}
              </code>
              <Button
                color="light"
                size="sm"
                onClick={() => setSecretVisible((v) => !v)}
                title={t(secretVisible ? "companySetting:sso.secret.hide" : "companySetting:sso.secret.show")}
              >
                {secretVisible ? <HiEyeOff className="h-4 w-4" /> : <HiEye className="h-4 w-4" />}
              </Button>
              <Button
                color="light"
                size="sm"
                onClick={() => copy(settings.tenant_secret as string)}
                title={t("companySetting:sso.secret.copy")}
              >
                <HiClipboardCopy className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center justify-between gap-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t("companySetting:sso.secret.rotateHint")}
              </p>
              <Button color="failure" size="sm" onClick={() => setConfirmRotate(true)}>
                <HiRefresh className="mr-2 h-4 w-4" />
                {t("companySetting:sso.secret.rotate")}
              </Button>
            </div>
          </>
        ) : (
          <Alert color="info">{t("companySetting:sso.secret.missing")}</Alert>
        )}
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("companySetting:sso.integration.title")}
        </h3>
        <dl className="text-sm">
          <div className="flex flex-col gap-1 border-b border-gray-100 py-2 dark:border-gray-700 md:flex-row md:gap-4">
            <dt className="w-48 shrink-0 text-gray-500 dark:text-gray-400">
              {t("companySetting:sso.integration.tenant")}
            </dt>
            <dd className="font-mono text-gray-900 dark:text-white">{settings.company_domain ?? "—"}</dd>
          </div>
          <div className="flex flex-col gap-1 border-b border-gray-100 py-2 dark:border-gray-700 md:flex-row md:gap-4">
            <dt className="w-48 shrink-0 text-gray-500 dark:text-gray-400">
              {t("companySetting:sso.integration.endpoint")}
            </dt>
            <dd className="break-all font-mono text-gray-900 dark:text-white">
              POST {loginUrlEndpoint}
            </dd>
          </div>
          <div className="flex flex-col gap-1 py-2 md:flex-row md:gap-4">
            <dt className="w-48 shrink-0 text-gray-500 dark:text-gray-400">
              {t("companySetting:sso.integration.credentials")}
            </dt>
            <dd className="text-gray-900 dark:text-white">
              <Badge color="info" className="inline">
                {t("companySetting:sso.integration.oauth")}
              </Badge>
            </dd>
          </div>
        </dl>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t("companySetting:sso.integration.hint")}
        </p>
      </Card>

      <Modal show={confirmRotate} size="md" onClose={() => setConfirmRotate(false)} popup>
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiExclamation className="mx-auto mb-4 h-14 w-14 text-red-500" />
            <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
              {t("companySetting:sso.secret.confirmTitle")}
            </h3>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
              {t("companySetting:sso.secret.confirmBody")}
            </p>
            <div className="flex justify-center gap-3">
              <Button color="failure" onClick={handleRotate} disabled={isRotating}>
                {isRotating ? <Spinner size="sm" /> : t("companySetting:sso.secret.confirmYes")}
              </Button>
              <Button color="gray" onClick={() => setConfirmRotate(false)}>
                {t("common:cancel")}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SsoSettingsTab;
